import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ContactsIcon from "@material-ui/icons/Contacts";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import EditIcon from "@material-ui/icons/Edit";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import NewTicketModal from "../../components/NewTicketModal";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
}));

const BusinessGroups = () => {
  const classes = useStyles();
  const history = useHistory();
  const [groups, setGroups] = useState([]);
  const [overview, setOverview] = useState(null);
  const [search, setSearch] = useState("");
  const [loadingOverviewId, setLoadingOverviewId] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [editForm, setEditForm] = useState({
    name: "",
    cnpjRoot: "",
    isActive: true,
  });

  const onlyDigits = (value = "") => value.replace(/\D/g, "");

  const loadGroups = async () => {
    try {
      const { data } = await api.get("/business-groups");
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleLoadOverview = async (groupId) => {
    try {
      setLoadingOverviewId(groupId);
      const { data } = await api.get(`/business-groups/${groupId}/overview`);
      setOverview(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingOverviewId(null);
    }
  };

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;

    return groups.filter((group) => {
      const bucket = [group.name, group.cnpjRoot].filter(Boolean).join(" ").toLowerCase();
      return bucket.includes(term);
    });
  }, [groups, search]);

  const openEditModal = (group) => {
    if (!group) return;
    setEditingGroup(group);
    setEditForm({
      name: group.name || "",
      cnpjRoot: onlyDigits(group.cnpjRoot || ""),
      isActive: Boolean(group.isActive),
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingGroup(null);
  };

  const handleSaveGroup = async () => {
    if (!editingGroup) return;

    try {
      setSavingEdit(true);
      await api.put(`/business-groups/${editingGroup.id}`, {
        name: editForm.name,
        cnpjRoot: onlyDigits(editForm.cnpjRoot || ""),
        isActive: Boolean(editForm.isActive),
      });

      toast.success("Grupo empresarial atualizado com sucesso.");
      closeEditModal();
      await loadGroups();
      if (overview?.group?.id === editingGroup.id) {
        await handleLoadOverview(editingGroup.id);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket && ticket.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  return (
    <MainContainer>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={handleCloseOrOpenTicket}
      />

      <MainHeader>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Title>Grupos Empresariais</Title>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar por nome ou raiz do CNPJ"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Raiz CNPJ</TableCell>
              <TableCell>Organizacoes</TableCell>
              <TableCell align="right">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredGroups.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhum grupo empresarial encontrado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {filteredGroups.map((group) => {
              const organizationsCount = Array.isArray(group.organizations)
                ? group.organizations.length
                : 0;

              return (
                <TableRow key={group.id}>
                  <TableCell>{group.id}</TableCell>
                  <TableCell>{group.name || "-"}</TableCell>
                  <TableCell>{group.cnpjRoot || "-"}</TableCell>
                  <TableCell>{organizationsCount}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => openEditModal(group)}
                      style={{ marginRight: 4 }}
                    >
                      <EditIcon color="secondary" fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleLoadOverview(group.id)}
                      disabled={loadingOverviewId === group.id}
                    >
                      Visao Geral
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {overview?.group && (
        <Paper className={classes.paper} variant="outlined">
          <Typography variant="h6" className={classes.sectionTitle}>
            Visao Geral: {overview.group.name}
          </Typography>

          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Organizacoes do Grupo
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Razao Social</TableCell>
                <TableCell>Nome Fantasia</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(overview.group.organizations || []).map((org) => (
                <TableRow key={org.id}>
                  <TableCell>{org.id}</TableCell>
                  <TableCell>{org.cnpj || "-"}</TableCell>
                  <TableCell>{org.razaoSocial || "-"}</TableCell>
                  <TableCell>{org.nomeFantasia || "-"}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color="default"
                      startIcon={<ContactsIcon />}
                      onClick={() => history.push(`/organizations/${org.id}/contacts`)}
                      style={{ marginRight: 8 }}
                    >
                      Contatos
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<AccountTreeIcon />}
                      onClick={() => history.push(`/organizations/${org.id}/sectors`)}
                    >
                      Setores
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="subtitle2" className={classes.sectionTitle}>
            Contatos Vinculados Diretamente ao Grupo
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contato</TableCell>
                <TableCell>Numero</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Papel</TableCell>
                <TableCell align="center">Conversa</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(overview.groupContacts || []).map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.contact?.name || "-"}</TableCell>
                  <TableCell>{item.contact?.number || "-"}</TableCell>
                  <TableCell>{item.contact?.email || "-"}</TableCell>
                  <TableCell>{item.roleTitle || "-"}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      disabled={!item.contact?.id}
                      onClick={() => {
                        if (!item.contact?.id) return;
                        setContactTicket({
                          ...item.contact,
                          channel: item.contact?.channel || "whatsapp",
                        });
                        setNewTicketModalOpen(true);
                      }}
                    >
                      <WhatsAppIcon style={{ color: "green" }} fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={editModalOpen} onClose={closeEditModal} fullWidth maxWidth="sm">
        <DialogTitle>Editar Grupo Empresarial</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome"
                value={editForm.name}
                onChange={(e) => {
                  const value = e?.target?.value || "";
                  setEditForm((prev) => ({ ...prev, name: value }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Raiz CNPJ"
                value={editForm.cnpjRoot}
                onChange={(e) => {
                  const value = onlyDigits(e?.target?.value || "");
                  setEditForm((prev) => ({ ...prev, cnpjRoot: value }));
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={(
                  <Checkbox
                    color="primary"
                    checked={Boolean(editForm.isActive)}
                    onChange={(e) => {
                      const checked = Boolean(e?.target?.checked);
                      setEditForm((prev) => ({ ...prev, isActive: checked }));
                    }}
                  />
                )}
                label="Ativo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} disabled={savingEdit}>Cancelar</Button>
          <Button color="primary" variant="contained" onClick={handleSaveGroup} disabled={savingEdit}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default BusinessGroups;